import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, TextField, Button, Stack, Alert, Chip, Divider, CircularProgress, MenuItem } from "@mui/material";
import AdminApi from "../../api/AdminApi";
import ApiInstance from "../../api/ApiInstance";

// Beheer-secties die de al bestaande admin-API eindelijk een UI geven
// (facturen, DAC7, gebruikers/restrictie, auditlog) + het nieuwe overzicht.
// Elke sectie is zelfvoorzienend (eigen fetch/foutafhandeling) zodat de
// Admin-pagina zelf alleen nog het zijmenu is.

const eurC = (c: number) => `€${(c / 100).toFixed(2)}`;

function StatCard({ value, label, warn, onClick }: { value: string | number; label: string; warn?: boolean; onClick?: () => void }) {
  return (
    <Box component={onClick ? "button" : "div"} onClick={onClick}
      sx={{ all: onClick ? "unset" : undefined, boxSizing: "border-box", fontFamily: "inherit", textAlign: "left",
        flex: "1 1 150px", maxWidth: 240, p: 2, borderRadius: "var(--radius-md)",
        bgcolor: warn ? "#fdecea" : "var(--surface)", border: "1px solid", borderColor: warn ? "#f5c6c0" : "var(--border)",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { borderColor: warn ? "error.main" : "primary.main" } : undefined,
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, m: 0, color: warn ? "error.main" : "primary.dark" }}>{value}</Typography>
      <Typography sx={{ fontSize: ".85rem", m: 0 }}>{label}</Typography>
    </Box>
  );
}

export type AdminOverviewData = Awaited<ReturnType<typeof AdminApi.overview>>;

/** Overzicht: opent met één gezondheidsstatement (hero als thesis) en
 *  klikbare kaarten die naar de relevante sectie springen. */
export function OverviewSection({ data, onNavigate }: { data: AdminOverviewData | null; onNavigate: (key: string) => void }) {
  const { t } = useTranslation();
  if (!data) return <CircularProgress size={24} sx={{ color: "primary.main" }} />;
  const run = data.billing.lastSettlementRun;
  const attention =
    (data.vetting.pending > 0 ? 1 : 0) +
    (data.compliance.open + data.compliance.overdue > 0 ? 1 : 0) +
    (data.organisations.fundingSuspended > 0 ? 1 : 0) +
    (run && !run.ok ? 1 : 0);
  return (
    <Stack spacing={2.5}>
      {/* De thesis: alles gezond, of N zaken die je aandacht vragen. */}
      <Typography variant="h3" sx={{ fontSize: "1.35rem", m: 0, color: attention === 0 ? "success.main" : "error.main" }}>
        {attention === 0 ? t("admin.ov.allHealthy") : t("admin.ov.needsAttention", { count: attention })}
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <StatCard value={data.vetting.pending} label={t("admin.ov.pendingVetting")} warn={data.vetting.pending > 0} onClick={() => onNavigate("psychologists")} />
        <StatCard value={data.compliance.open + data.compliance.overdue} label={t("admin.ov.complianceOpen")} warn={data.compliance.overdue > 0} onClick={() => onNavigate("compliance")} />
        <StatCard value={data.billing.openInvoices} label={t("admin.ov.openInvoices")} onClick={() => onNavigate("invoices")} />
        <StatCard value={data.organisations.fundingSuspended} label={t("admin.ov.suspendedOrgs")} warn={data.organisations.fundingSuspended > 0} onClick={() => onNavigate("organisations")} />
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <StatCard value={data.users.clients} label={t("admin.ov.clients")} onClick={() => onNavigate("users")} />
        <StatCard value={data.users.psychologists} label={t("admin.ov.psychologists")} onClick={() => onNavigate("psychologists")} />
        <StatCard value={data.vetting.verified} label={t("admin.ov.verified")} onClick={() => onNavigate("psychologists")} />
        <StatCard value={`${data.organisations.total} / ${data.organisations.memberships}`} label={t("admin.ov.orgsMembers")} onClick={() => onNavigate("organisations")} />
      </Box>
      {run && (
        <Alert severity={run.ok ? "success" : "error"} sx={{ cursor: "pointer" }} onClick={() => onNavigate("settlement")}>
          {t("admin.ov.lastRun", { period: `${run.year}-${String(run.month).padStart(2, "0")}`, when: new Date(run.ranAt).toLocaleString() })}
          {!run.ok && ` — ${t("admin.watchdogAttention")}`}
        </Alert>
      )}
    </Stack>
  );
}

/** Facturen: lijst + uitgeven / betaald markeren / bekijken / Peppol / runs. */
export function InvoicesSection() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Awaited<ReturnType<typeof AdminApi.listInvoices>>["invoices"]>([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const now = new Date();
  const [runYear, setRunYear] = useState(now.getFullYear());
  const [runMonth, setRunMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const load = useCallback(() => { AdminApi.listInvoices().then((r) => setInvoices(r.invoices)).catch(() => setErr(t("admin.inv.loadError"))); }, [t]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    setErr(""); setMsg("");
    try { await fn(); setMsg(okMsg); load(); }
    catch (e: any) { setErr(e?.response?.data?.error || t("admin.inv.actionError")); }
  };
  const viewHtml = async (id: string) => {
    const res = await ApiInstance.getApi().get(`/admin/billing/invoices/${id}/html`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data as Blob], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
        <TextField select size="small" label={t("orgReport.yearLabel")} value={runYear} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRunYear(Number(e.target.value))} sx={{ width: 110 }}>
          {[now.getFullYear() - 1, now.getFullYear()].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t("admin.inv.month")} value={runMonth} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRunMonth(Number(e.target.value))} sx={{ width: 110 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
        <Button size="small" variant="outlined" onClick={() => act(async () => {
          const r = await AdminApi.runMonthlyInvoices(runYear, runMonth);
          setMsg(t("admin.inv.runDone", { created: r.created.length, skipped: r.skipped }));
        }, "")}>{t("admin.inv.runMonthly")}</Button>
      </Stack>
      {msg && <Alert severity="success" onClose={() => setMsg("")}>{msg}</Alert>}
      {err && <Alert severity="error" onClose={() => setErr("")}>{err}</Alert>}
      {invoices.length === 0 ? (
        <Typography sx={{ color: "text.secondary", m: 0 }}>{t("admin.inv.none")}</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {invoices.map((inv) => (
            <Box key={inv.id} sx={{ py: 1.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 600, m: 0, minWidth: 150 }}>{inv.number}</Typography>
                <Chip size="small" variant="outlined" label={t(`admin.inv.type.${inv.type}`)} />
                <Chip size="small" label={t(`admin.inv.status.${inv.status}`)} color={inv.status === "paid" ? "success" : inv.status === "issued" ? "warning" : "default"} />
                {inv.peppolStatus === "sent" && <Chip size="small" label="Peppol ✓" color="success" variant="outlined" />}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ m: 0, fontWeight: 600 }}>{eurC(inv.totals.payableCents)}</Typography>
              </Box>
              <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0, mt: 0.25 }}>
                {inv.counterparty.name} · {inv.period.year}{inv.period.month ? `-${String(inv.period.month).padStart(2, "0")}` : ""}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", gap: 1 }}>
                <Button size="small" onClick={() => viewHtml(inv.id)}>{t("admin.inv.view")}</Button>
                {inv.status === "draft" && <Button size="small" variant="outlined" onClick={() => act(() => AdminApi.issueInvoice(inv.id), t("admin.inv.issued"))}>{t("admin.inv.issue")}</Button>}
                {inv.status === "issued" && <Button size="small" variant="outlined" onClick={() => act(() => AdminApi.markInvoicePaid(inv.id), t("admin.inv.markedPaid"))}>{t("admin.inv.markPaid")}</Button>}
                {inv.status === "issued" && inv.counterparty.kind === "organisation" && inv.peppolStatus !== "sent" && (
                  <Button size="small" variant="outlined" onClick={() => act(() => AdminApi.sendInvoicePeppol(inv.id), t("admin.inv.peppolSent"))}>{t("admin.inv.sendPeppol")}</Button>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** DAC7: volledigheid per verkoper + herinneringen. */
export function Dac7Section() {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  // Uitleg eerst (feedback: "aan wie stuur ik een herinnering?"): de flow is
  // wettelijk voorgeschreven — zie de intro-tekst in de render.
  const [rows, setRows] = useState<Awaited<ReturnType<typeof AdminApi.dac7Completeness>>["incomplete"]>([]);
  const [msg, setMsg] = useState("");
  const load = useCallback((y: number) => { AdminApi.dac7Completeness(y).then((r) => setRows(r.incomplete)).catch(() => setRows([])); }, []);
  useEffect(() => { load(year); }, [year, load]);
  const incomplete = rows.filter((r) => r.missing.length > 0);
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t("admin.dac7.flowExplain")}</Alert>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <TextField select size="small" label={t("orgReport.yearLabel")} value={year} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setYear(Number(e.target.value))} sx={{ width: 110 }}>
          {[new Date().getFullYear() - 1, new Date().getFullYear()].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <Typography sx={{ m: 0, color: "text.secondary", fontSize: ".9rem" }}>{t("admin.dac7.summary", { total: rows.length, incomplete: incomplete.length })}</Typography>
      </Stack>
      {msg && <Alert severity="success" onClose={() => setMsg("")}>{msg}</Alert>}
      {incomplete.length === 0 ? (
        <Alert severity="success">{t("admin.dac7.allComplete")}</Alert>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {incomplete.map((r) => (
            <Box key={r.profileId} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 600, m: 0, minWidth: 160 }}>{r.displayName}</Typography>
              <Typography sx={{ m: 0, flex: 1, color: "text.secondary", fontSize: ".9rem" }}>{t("admin.dac7.missing")}: {r.missing.join(", ")}</Typography>
              {r.suspended && <Chip size="small" color="error" label={t("admin.dac7.suspended")} />}
              <Typography sx={{ m: 0, fontSize: ".85rem", color: "text.secondary" }}>{t("admin.dac7.reminders", { n: r.reminderCount || 0 })}</Typography>
              <Button size="small" variant="outlined" onClick={() => AdminApi.dac7Remind(r.profileId).then(() => { setMsg(t("admin.dac7.reminded", { name: r.displayName })); load(year); })}>
                {t("admin.dac7.remind")}
              </Button>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Gebruikers: opzoeken op e-mail (support/DSAR) + art. 18-restrictie. */
export function UsersSection() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<Awaited<ReturnType<typeof AdminApi.searchUsers>>["users"]>([]);
  const [searched, setSearched] = useState(false);
  const [err, setErr] = useState("");
  const search = async () => {
    setErr("");
    try { setUsers((await AdminApi.searchUsers(query.trim())).users); setSearched(true); }
    catch (e: any) { setErr(e?.response?.data?.error || t("admin.usr.searchError")); }
  };
  const toggleRestriction = async (id: string, active: boolean) => {
    const reason = active ? window.prompt(t("admin.usr.restrictReason")) || undefined : undefined;
    await AdminApi.setUserRestriction(id, active, reason);
    await search();
  };
  return (
    <Stack spacing={2}>
      <Typography sx={{ m: 0, fontSize: ".9rem", color: "text.secondary" }}>{t("admin.usr.hint")}</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <TextField size="small" label={t("admin.usr.searchLabel")} value={query} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter" && query.trim().length >= 3) search(); }} sx={{ minWidth: 280 }} />
        <Button size="small" variant="outlined" onClick={search} disabled={query.trim().length < 3}>{t("admin.usr.search")}</Button>
      </Stack>
      {err && <Alert severity="error" onClose={() => setErr("")}>{err}</Alert>}
      {searched && users.length === 0 && <Typography sx={{ color: "text.secondary", m: 0 }}>{t("admin.usr.none")}</Typography>}
      <Stack divider={<Divider />} spacing={0}>
        {users.map((u) => (
          <Box key={u.id} sx={{ py: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 600, m: 0 }}>{u.email}</Typography>
              <Chip size="small" variant="outlined" label={u.role} />
              {u.mfaEnabled && <Chip size="small" label="MFA" color="success" variant="outlined" />}
              {u.restricted && <Chip size="small" label={t("admin.usr.restricted")} color="error" />}
            </Box>
            <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0, mt: 0.25 }}>
              {u.name || "—"} · {t("admin.usr.since", { date: new Date(u.createdAt).toLocaleDateString() })}
              {u.orgNames.length > 0 && ` · ${u.orgNames.join(", ")}`}
            </Typography>
            <Box sx={{ mt: 0.75 }}>
              <Button size="small" variant="outlined" color={u.restricted ? "primary" : "warning"} onClick={() => toggleRestriction(u.id, !u.restricted)}>
                {u.restricted ? t("admin.usr.unrestrict") : t("admin.usr.restrict")}
              </Button>
            </Box>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

/** Auditlog-viewer: accountability (art. 5(2)) + incidentonderzoek. */
export function AuditSection() {
  const { t } = useTranslation();
  const [action, setAction] = useState("");
  const [entries, setEntries] = useState<Awaited<ReturnType<typeof AdminApi.auditLog>>["entries"]>([]);
  const load = useCallback((a: string) => { AdminApi.auditLog(100, a).then((r) => setEntries(r.entries)).catch(() => setEntries([])); }, []);
  useEffect(() => { load(""); }, [load]);
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <TextField size="small" label={t("admin.audit.filter")} placeholder="bv. gdpr / admin / auth" value={action}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAction(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") load(action); }} sx={{ minWidth: 260 }} />
        <Button size="small" variant="outlined" onClick={() => load(action)}>{t("admin.audit.apply")}</Button>
      </Stack>
      {entries.length === 0 ? (
        <Typography sx={{ color: "text.secondary", m: 0 }}>{t("admin.audit.none")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem", minWidth: 640, "& td, & th": { textAlign: "left", py: 0.5, pr: 2, borderBottom: "1px solid var(--border)" }, "& th": { color: "text.secondary" } }}>
            <thead><tr><th>{t("admin.audit.when")}</th><th>{t("admin.audit.action")}</th><th>{t("admin.audit.actor")}</th><th>{t("admin.audit.target")}</th><th>{t("admin.audit.outcome")}</th></tr></thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(e.at).toLocaleString()}</td>
                  <td>{e.action}</td>
                  <td>{e.actorRole || "—"}</td>
                  <td>{e.targetType ? `${e.targetType} ${String(e.targetId || "").slice(-6)}` : "—"}</td>
                  <td>{e.outcome}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
